import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import { FaApple, FaCheckCircle, FaGlobe, FaGooglePlay } from 'react-icons/fa';
import Seo from '../components/Seo';
import AppStoreButtons from '../components/release/AppStoreButtons';
import { PLATFORM } from '../config/platform';
import practiceRoomUi from '../assets/android/practice-room-ui.png';
import quickRaceIcon from '../assets/android/quick-race.svg';
import practiceRoomIcon from '../assets/android/practice-room.svg';
import './MarketingPages.css';

const appSchema = {
  '@context': 'https://schema.org',
  '@type': 'SoftwareApplication',
  name: PLATFORM.name,
  alternateName: 'P4R',
  operatingSystem: 'iOS, Android, Web',
  applicationCategory: 'SocialNetworkingApplication',
  applicationSubCategory: 'Pool and billiards sports community',
  downloadUrl: [PLATFORM.ios.url, PLATFORM.android.url],
  offers: { '@type': 'Offer', price: '0', priceCurrency: 'USD' },
  description: 'A pool and billiards platform for nearby player discovery, the Android P4R Engine Practice Room, advanced Race Room scoring, teams, tournaments, venues, messaging, business tools and Shoparound.',
  featureList: [
    'Nearby pool player discovery',
    'Player messaging and real-world match organization',
    'P4R Engine Android Practice Room with multi-discipline physics and training',
    'Advanced Race Room pool scorekeeper, timers, innings, shared scoring and live display',
    'Pool teams and tournament tools',
    'Pool hall and billiards business discovery',
    'Billiards social feed and marketplace',
  ],
};

export default function AppDownloadPage() {
  const location = useLocation();
  const androidPage = location.pathname === '/android' || location.pathname === '/android-beta';
  const seoTitle = androidPage
    ? 'Pool 4 Real for Android | P4R Engine Practice Room & Race Room'
    : 'Download Pool 4 Real | Billiards Practice, Race Room & Player Community';
  const seoDescription = androidPage
    ? 'Download Pool 4 Real for Android with the P4R Engine Practice Room, 9 Ball, 8 Ball, 3 Cushion, Brazilian Sinuca, shot prediction, drills, gameplay audio, advanced Race Room, teams, tournaments and player discovery.'
    : 'Download Pool 4 Real for iPhone or Android. Train with the Android P4R Engine, run advanced Race Room matches, meet nearby pool players, join tournaments, discover pool halls and connect with the billiards community.';
  return (
    <main className="p4r-marketing-page">
      <Seo
        title={seoTitle}
        description={seoDescription}
        path={androidPage ? '/android' : '/download'}
        schema={appSchema}
      />
      <section className="p4r-marketing-hero centered">
        <img className="p4r-app-page-icon" src="/assets/pool4real-app-icon.png" alt="Pool 4 Real pool and billiards social app icon" />
        <p className="p4r-marketing-kicker">Official Pool 4 Real mobile apps</p>
        <h1>{androidPage ? 'The most complete Pool 4 Real experience is on Android.' : 'Take your billiards community, practice, and match tools with you.'}</h1>
        <p>{androidPage ? 'Train with the P4R Engine Practice Room, run advanced Race Room sessions, find nearby players, message, build teams, enter tournaments, discover venues, and manage more of your billiards life from one Android app.' : 'Pool 4 Real combines real-world player/community tools with advanced Android practice and match technology. Find players, train, arrange matches, keep score, join teams and tournaments, discover venues, message the community, and browse pool equipment.'}</p>
        <AppStoreButtons source="download_page" />
      </section>

      <section className="p4r-platform-grid">
        <article>
          <FaApple />
          <h2>Pool 4 Real for iPhone</h2>
          <p>Connect with nearby pool players, organize your next match, follow your teams and tournaments, and use P4R tools wherever you play.</p>
          <a href={PLATFORM.ios.url} target="_blank" rel="noopener noreferrer">View Pool 4 Real in the App Store</a>
        </article>
        <article>
          <FaGooglePlay />
          <h2>Pool 4 Real for Android</h2>
          <p>Use the Android reference experience for the P4R Engine Practice Room, advanced Race Room, player discovery, messaging, tournaments, teams, businesses, Shoparound, and the newest P4R workflows.</p>
          <a href={PLATFORM.android.url} target="_blank" rel="noopener noreferrer">View Pool 4 Real on Google Play</a>
        </article>
        <article>
          <FaGlobe />
          <h2>Continue on the web</h2>
          <p>Sign in with the same P4R account to use supported community, match, tournament, marketplace, and business features in your browser.</p>
          <Link to="/login">Sign in to Pool 4 Real on the web</Link>
        </article>
      </section>

      {androidPage && <section className="p4r-android-download-feature" aria-labelledby="android-engine-heading">
        <div className="p4r-android-download-copy">
          <p className="p4r-marketing-kicker">Android reference experience</p>
          <h2 id="android-engine-heading">P4R Engine Practice Room + advanced Race Room</h2>
          <div className="p4r-android-tool-row"><img src={practiceRoomIcon} alt="" /><span><strong>Practice Room</strong><small>9 Ball, 8 Ball, 3 Cushion, Brazilian Sinuca, additional modes, shot prediction, Auto Aim, drills, spin/elevation control, discipline-specific physics and match-tuned gameplay audio.</small></span></div>
          <div className="p4r-android-tool-row"><img src={quickRaceIcon} alt="" /><span><strong>Race Room</strong><small>Racks, points, innings, asymmetric targets, shot timer, match clock, player turns, undo/history, shared scoring modes and a full-screen live table display.</small></span></div>
          <div className="p4r-home-actions"><Link className="p4r-home-secondary" to="/pool-practice-room">Practice Room details</Link><Link className="p4r-home-secondary" to="/pool-scorekeeper">Race Room details</Link></div>
        </div>
        <div className="p4r-android-download-visual"><img src={practiceRoomUi} alt="Pool 4 Real Android P4R Engine Practice Room interface" /></div>
      </section>}

      <section className="p4r-marketing-content">
        <h2>Built around everything that happens before, during, and after a real pool match</h2>
        <div className="p4r-check-grid">
          {[
            'Find nearby pool and billiards players by location',
            'Message players and organize in-person games',
            'Train shots in the Android P4R Engine Practice Room with multiple billiards disciplines',
            'Count racks, races, points, innings, turns, shot time, and match time in advanced Race Room',
            'Create teams, manage rosters, and share announcements',
            'Discover tournaments, enter events, check in, and follow brackets',
            'Find pool halls, billiards businesses, events, and services',
            'Share Pockits and build a recognizable player profile',
            'Buy and sell cues, cases, tables, and pool equipment in Shoparound',
          ].map((item) => <p key={item}><FaCheckCircle /> {item}</p>)}
        </div>
      </section>
    </main>
  );
}
